import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Profile } from '@/types/database';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import OneSignal from 'onesignal-cordova-plugin';
import { Purchases } from '@revenuecat/purchases-capacitor';
import {
  cacheAuthSession,
  getCachedAuthSession,
  clearCachedAuthSession,
  cacheProfile,
  getCachedProfile,
} from '@/lib/offlineDataCache';
import { getStoredReferralCode, clearStoredReferralCode } from '@/lib/referral';
import { clearImageCache } from '@/lib/imageCache';
import { APP_URL } from '@/lib/urls';


interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PENDING_FULL_NAME_KEY = 'orcapro_pending_full_name';
const FIRST_LOGIN_KEY = 'orcazap_first_login_pending';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (data && !error) {
        setProfile(data as Profile);
        // Cache profile for offline access
        cacheProfile(userId, data);
      }
    } catch (e) {
      console.error('[AuthContext] Error fetching profile:', e);
      // Try to load from cache on network error
      const cachedProfile = getCachedProfile(userId);
      if (cachedProfile) {
        console.log('[AuthContext] Using cached profile');
        setProfile(cachedProfile as Profile);
      }
    }
  };

  // Attribute a referral captured from a ?ref= link (stored in sessionStorage).
  // This is the ONLY client entry point for referral rewards: the reward fields
  // (referred_by / bonus_quotes) can only be written by the server via the
  // process_referral_reward RPC (client writes are blocked by a DB trigger), and
  // the RPC requires an authenticated session — so it must run after SIGNED_IN,
  // not during signUp (where email-confirmation flows have no session yet). This
  // also transparently covers OAuth signups.
  const processStoredReferral = async (userId: string) => {
    const referralCode = getStoredReferralCode();
    if (!referralCode) return;

    try {
      const { data, error } = await supabase.functions.invoke('process-referral', {
        body: { referred_user_id: userId, referral_code: referralCode },
      });

      if (error) {
        // Non-2xx (401/403/5xx) — likely transient/auth timing. Keep the code so
        // a later SIGNED_IN can retry instead of silently dropping the referral.
        console.warn('[AuthContext] Referral processing failed, will retry later:', error.message);
        return;
      }

      // The edge function returns 200 for terminal rejections too
      // (invalid/expired code, already referred, self-referral, >7d account…).
      // Whether it succeeded or was terminally rejected, there is nothing to
      // retry, so clear the stored code.
      clearStoredReferralCode();

      if (data?.success) {
        console.log('[AuthContext] Referral processed:', data);
        // Reflect the +3 bonus quotes granted to the referred user.
        await fetchProfile(userId);
      } else {
        console.info('[AuthContext] Referral not applied:', data?.code);
      }
    } catch (e) {
      // Network error (offline) — keep the code for a future retry.
      console.warn('[AuthContext] Referral processing error, will retry later:', e);
    }
  };

  const syncPendingProfileName = async (userId: string) => {
    const pendingFullName = localStorage.getItem(PENDING_FULL_NAME_KEY);
    if (!pendingFullName) return;

    const { error } = await supabase
      .from('profiles')
      .update({ full_name: pendingFullName })
      .eq('id', userId);

    if (error) {
      console.error('Error syncing pending profile name:', error);
      return;
    }

    localStorage.removeItem(PENDING_FULL_NAME_KEY);
    await fetchProfile(userId);
  };

  // Sync OAuth profile name (Google, etc.) if profile full_name is empty
  const syncOAuthProfileName = async (userId: string, userMetadata: any) => {
    // Check if we have a name from OAuth metadata
    const oauthFullName = userMetadata?.full_name || userMetadata?.name;
    if (!oauthFullName) return;

    // Fetch current profile to check if name is already set
    const { data: currentProfile } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', userId)
      .maybeSingle();

    // Only update if profile exists and full_name is empty
    if (currentProfile && !currentProfile.full_name) {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: oauthFullName })
        .eq('id', userId);

      if (error) {
        console.error('Error syncing OAuth profile name:', error);
        return;
      }

      console.log('[AuthContext] OAuth profile name synced:', oauthFullName);
      await fetchProfile(userId);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log(`[AuthContext] onAuthStateChange event: ${event}, hasSession: ${!!session}`);
        
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Cache session for offline access
          cacheAuthSession({
            userId: session.user.id,
            email: session.user.email,
            userMetadata: session.user.user_metadata,
          });
          
          setTimeout(() => {
            fetchProfile(session.user.id);
            syncPendingProfileName(session.user.id);

            // Attribute a pending referral once the user is authenticated.
            if (event === 'SIGNED_IN') {
              processStoredReferral(session.user.id);
            }

            // Sync OAuth profile name (Google, etc.) if profile full_name is empty
            if (session.user.user_metadata) {
              syncOAuthProfileName(session.user.id, session.user.user_metadata);
            }

            // Set OneSignal External User ID when user logs in
            if (Capacitor.isNativePlatform()) {
              try {
                OneSignal.login(session.user.id);
                console.log('[AuthContext] OneSignal: External User ID set to', session.user.id);
              } catch (e) {
                console.error('[AuthContext] Error setting OneSignal External User ID:', e);
              }

              // Identify the user in RevenueCat so purchases/webhooks carry the Supabase user id
              Purchases.logIn({ appUserID: session.user.id })
                .then(() => {
                  console.log('[AuthContext] RevenueCat: logged in with appUserID', session.user.id);
                })
                .catch((e) => {
                  console.error('[AuthContext] Error logging into RevenueCat:', e);
                });
            }

            // Importing a visitor's guest quote is handled by
            // <GuestQuoteImportPrompt/> after login (it asks the user first and
            // resolves a company_id for RLS), so nothing to do here.
          }, 0);
        } else {
          setProfile(null);
          // Clear cached session on logout
          clearCachedAuthSession();

          // Clear OneSignal External User ID when user logs out
          if (Capacitor.isNativePlatform()) {
            try {
              OneSignal.logout();
              console.log('[AuthContext] OneSignal: User logged out');
            } catch (e) {
              console.error('[AuthContext] Error logging out from OneSignal:', e);
            }

            Purchases.logOut()
              .then(() => {
                console.log('[AuthContext] RevenueCat: logged out (anonymous again)');
              })
              .catch((e) => {
                console.error('[AuthContext] Error logging out from RevenueCat:', e);
              });
          }
        }
      }
    );

    // THEN check for existing session with timeout for offline support
    const OFFLINE_TIMEOUT = 3000; // 3 seconds timeout
    
    // Try to load from cache immediately for offline scenario
    const cachedAuth = getCachedAuthSession();
    let offlineTimeoutTriggered = false;
    
    const offlineTimeout = setTimeout(() => {
      // If we haven't got a response and have cached auth, use it
      if (cachedAuth && loading) {
        console.log('[AuthContext] Offline timeout - using cached auth session');
        offlineTimeoutTriggered = true;
        
        // Create a minimal user-like object from cached session
        const cachedUser = {
          id: cachedAuth.userId,
          email: cachedAuth.email || '',
          user_metadata: cachedAuth.userMetadata || {},
        } as unknown as User;
        
        setUser(cachedUser);
        
        // Load cached profile
        const cachedProfile = getCachedProfile(cachedAuth.userId);
        if (cachedProfile) {
          setProfile(cachedProfile as Profile);
        }
        
        setLoading(false);
      }
    }, OFFLINE_TIMEOUT);
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(offlineTimeout);
      
      // Skip if offline timeout already triggered
      if (offlineTimeoutTriggered) {
        console.log('[AuthContext] getSession returned after offline timeout, refreshing with real session');
      }
      
      console.log(`[AuthContext] getSession result: hasSession: ${!!session}`);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Cache session for offline access
        cacheAuthSession({
          userId: session.user.id,
          email: session.user.email,
          userMetadata: session.user.user_metadata,
        });
        
        fetchProfile(session.user.id);
        syncPendingProfileName(session.user.id);
      }
      setLoading(false);
    }).catch((error) => {
      clearTimeout(offlineTimeout);
      console.error('[AuthContext] getSession error:', error);
      
      // On error, try to use cached session
      if (cachedAuth && !offlineTimeoutTriggered) {
        console.log('[AuthContext] Using cached auth session after error');
        const cachedUser = {
          id: cachedAuth.userId,
          email: cachedAuth.email || '',
          user_metadata: cachedAuth.userMetadata || {},
        } as unknown as User;
        
        setUser(cachedUser);
        
        const cachedProfile = getCachedProfile(cachedAuth.userId);
        if (cachedProfile) {
          setProfile(cachedProfile as Profile);
        }
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Realtime subscription to profile changes (e.g. plan upgrade)
  useEffect(() => {
    if (!user?.id) return;

    console.log('[AuthContext] Setting up profile realtime subscription for user:', user.id);
    
    const channel = supabase
      .channel(`profile-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[AuthContext] Profile updated via realtime:', payload.new);
          const updatedProfile = payload.new as Profile;
          setProfile(updatedProfile);
          cacheProfile(user.id, updatedProfile);
        }
      )
      .subscribe((status, err) => {
        console.log(`[AuthContext] Profile realtime status for ${user.id}:`, status, err);
      });

    return () => {
      console.log('[AuthContext] Cleaning up profile realtime subscription for user:', user.id);
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const signUp = async (email: string, password: string, fullName?: string) => {
    // Fallback link target for the confirmation email. Point it at /auth/callback
    // (the page built to confirm and, on mobile, bridge back into the app via deep
    // link) instead of "/", which relied on detectSessionInUrl + the redirect
    // allowlist and could land logged-out. On native the origin is localhost, so
    // force the production web URL (same pattern as password reset). The primary
    // path is the in-app OTP code — this only matters if the user clicks the link.
    const redirectBase = Capacitor.isNativePlatform() ? APP_URL : window.location.origin;
    const redirectUrl = `${redirectBase}/auth/callback`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || null,
        },
      },
    });

    // If signup successful, set the first login flag
    // This flag will be checked when user confirms email and logs in for the first time
    if (!error && data.user) {
      localStorage.setItem(FIRST_LOGIN_KEY, 'true');
    }

    // If signup successful and user exists, persist full_name to profiles.
    // Note: when email confirmation is enabled, the user may not have a session yet,
    // so this write can fail due to RLS. In that case we keep a pending value and
    // apply it on first SIGNED_IN.
    if (!error && data.user && fullName) {
      const userId = data.user.id;
      let didPersist = false;

      const { data: updatedRows, error: updateError } = await supabase
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', userId)
        .select('id');

      if (!updateError && updatedRows && updatedRows.length > 0) {
        didPersist = true;
      } else if (updateError) {
        console.error('Error updating profile after signup:', updateError);
      }

      if (!didPersist) {
        const { error: upsertError } = await supabase
          .from('profiles')
          .upsert({ id: userId, email, full_name: fullName }, { onConflict: 'id' });

        if (!upsertError) {
          didPersist = true;
        } else {
          // Esperado quando a confirmação de e-mail está ligada: sem sessão ainda,
          // o RLS bloqueia a escrita no profiles. A linha já existe (trigger
          // handle_new_user) e o full_name é aplicado no 1º SIGNED_IN pelo
          // fallback (PENDING_FULL_NAME_KEY / syncPendingProfileName). Logamos como
          // warn — não é erro real e não deve virar alarme no Sentry.
          console.warn(
            '[AuthContext] full_name não persistido no signup (esperado: RLS bloqueia sem sessão antes de confirmar o e-mail); será aplicado no 1º login.',
            (upsertError as { message?: string })?.message ?? upsertError,
          );
        }
      }

      if (!didPersist) {
        localStorage.setItem(PENDING_FULL_NAME_KEY, fullName);
      }
    }
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);

    // Wipe cached image bytes (IndexedDB) so they don't leak to the next account
    // on a shared device. Fire-and-forget: it must not delay the logout.
    void clearImageCache();

    // Clear all user-specific localStorage data (preserve only first visit flag)
    const firstVisit = localStorage.getItem('orcazap_first_visit');
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key !== 'orcazap_first_visit') {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (firstVisit) {
      localStorage.setItem('orcazap_first_visit', firstVisit);
    }
    
    // Dispatch event to notify components to reset to defaults
    window.dispatchEvent(new CustomEvent('user-logout'));
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
